import { useEffect, useState } from 'react';

/** Free, CORS-enabled IP → country lookup (no API key). Returns ISO 3166-1 alpha-2. */
const GEO_ENDPOINT = 'https://get.geojs.io/v1/ip/country.json';
/** Give up on the lookup after this long — a blocked/hung request must not stall the banner. */
const LOOKUP_TIMEOUT_MS = 3000;

export interface VisitorCountry {
  /** ISO alpha-2 country code (e.g. "US", "CN"), or `null` when unknown/unresolved. */
  country: string | null;
  /**
   * `true` once the lookup has settled (success, error, or timeout). Consumers
   * should wait for this before deciding, so a fail-open banner doesn't flash
   * for someone we'd otherwise exclude.
   */
  resolved: boolean;
}

/**
 * Resolves the visitor's country from their IP via a client-side geo endpoint.
 * IP-only by design (a VPN out of China also unlocks Play). Fails open: on
 * error / non-2xx / timeout, `country` stays `null` and `resolved` becomes
 * `true` so callers show the banner rather than hide it.
 */
export function useVisitorCountry(): VisitorCountry {
  const [country, setCountry] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort(),
      LOOKUP_TIMEOUT_MS
    );

    fetch(GEO_ENDPOINT, { signal: controller.signal })
      .then((res) =>
        res.ok ? res.json() : Promise.reject(new Error('bad status'))
      )
      .then((data: { country?: string }) => {
        if (!cancelled && data?.country) {
          setCountry(data.country.toUpperCase());
        }
      })
      .catch(() => {
        // Fail open: leave `country` null so the caller can still show the banner.
      })
      .finally(() => {
        if (!cancelled) setResolved(true);
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  return { country, resolved };
}
